import {
  Editor as SlateEditor,
  Element as SlateElement,
  Node as SlateNode,
  Range as SlateRange,
} from 'slate';

import { SLATE_TEXT_FORMATS } from './const';

export const getEditorParagraphs = (editor) =>
  SlateEditor.nodes(editor, {
    at: [],
    match: (node) => SlateElement.isElement(node) && node.type === SLATE_TEXT_FORMATS.PARAGRAPH,
  });

/**
 * Get Slate top-level block for the passed location
 * @param {import('slate').Editor} editor - Slate editor instance
 * @param {import('slate').Location} at - Slate location
 * @returns {import('slate').BaseNode|undefined} - Slate block
 */
export const getSlateParentBlock = (editor, at) => {
  const entry = SlateEditor.above(editor, {
    at,
    match: (node) => !SlateEditor.isEditor(node) && SlateEditor.isBlock(editor, node),
    mode: 'highest',
  });
  return entry?.[0];
};

/**
 * Get text of the Slate top-level block for the passed location
 * @param {import('slate').Editor} editor - Slate editor instance
 * @param {import('slate').Location} at - Slate location
 * @returns {string} - Block text
 */
export const getSlateBlockText = (editor, at) => {
  const blockNode = getSlateParentBlock(editor, at);
  return blockNode ? SlateNode.string(blockNode) : '';
};

/**
 * Get Slate range before the passed location
 * @param {import('slate').Editor} editor - Slate editor instance
 * @param {import('slate').Location} location - Slate location
 * @returns {import('slate').BaseRange} - Slate range
 */
const getSlateRangeBefore = (editor, location) => {
  const startPoint = SlateEditor.start(editor, []);
  return SlateEditor.range(editor, startPoint, location);
};

/**
 * Get Slate range after the passed location
 * @param {import('slate').Editor} editor - Slate editor instance
 * @param {import('slate').Location} location - Slate location
 * @returns {import('slate').BaseRange} - Slate range
 */
const getSlateRangeAfter = (editor, location) => {
  const endPoint = SlateEditor.end(editor, []);
  return SlateEditor.range(editor, location, endPoint);
};

/**
 * Get text value before the passed location
 * @param {import('slate').Editor} editor - Slate editor instance
 * @param {import('slate').Location} location - Slate location
 * @returns {string|null} - Text value
 */
export const getSlateTextBefore = (editor, location) => {
  if (!editor || !location) {
    return null;
  }

  const beforeRange = getSlateRangeBefore(editor, location);
  return SlateEditor.string(editor, beforeRange);
};

/**
 * Get text value after the passed location
 * @param {import('slate').Editor} editor - Slate editor instance
 * @param {import('slate').Location} location - Slate location
 * @returns {string|null} - Text value
 */
export const getSlateTextAfter = (editor, location) => {
  if (!editor || !location) {
    return null;
  }

  const afterRange = getSlateRangeAfter(editor, location);
  return SlateEditor.string(editor, afterRange);
};

/**
 * Get Slate fragment before the passed location
 * @param {import('slate').Editor} editor - Slate editor instance
 * @param {import('slate').Location} location - Slate location
 * @returns {import('slate').Descendant[]|null} - Slate fragment
 */
export const getSlateFragmentBefore = (editor, location) => {
  if (!editor || !location) {
    return null;
  }

  const beforeRange = getSlateRangeBefore(editor, location);
  return SlateNode.fragment(editor, beforeRange);
};

/**
 * Get Slate fragment after the passed location
 * @param {import('slate').Editor} editor - Slate editor instance
 * @param {import('slate').Location} location - Slate location
 * @returns {import('slate').Descendant[]|null} - Slate fragment
 */
export const getSlateFragmentAfter = (editor, location) => {
  if (!editor || !location) {
    return null;
  }

  const afterRange = getSlateRangeAfter(editor, location);
  return SlateNode.fragment(editor, afterRange);
};

/**
 * Get text value of the passed range
 * @param {import('slate').Editor} editor - Slate editor instance
 * @param {import('slate').Range} range - Slate range
 * @returns {string} - Text value
 */
export const getSlateRangeText = (editor, range) => {
  if (!range || SlateRange.isCollapsed(range)) {
    return '';
  }

  return SlateEditor.string(editor, range);
};

/**
 * Get text value before the passed range
 * @param {import('slate').Editor} editor - Slate editor instance
 * @param {import('slate').Range} range - Slate range
 * @returns {string} - Text value
 */
export const getSlateTextBeforeRange = (editor, range) => {
  if (!range) {
    return '';
  }

  const rangeEdges = SlateRange.edges(range);
  return getSlateTextBefore(editor, rangeEdges[0]);
};

/**
 * Get text value after the passed range
 * @param {import('slate').Editor} editor - Slate editor instance
 * @param {import('slate').Range} range - Slate range
 * @returns {string} - Text value
 */
export const getSlateTextAfterRange = (editor, range) => {
  if (!range) {
    return '';
  }

  const rangeEdges = SlateRange.edges(range);
  return getSlateTextAfter(editor, rangeEdges[1]);
};

/**
 * Get Slate fragment from the current selection
 * @param {import('slate').Editor} editor - Slate editor instance
 * @returns {import('slate').Descendant[]} - Slate fragment
 */
export const getSlateSelectionFragment = (editor) => {
  if (!editor?.selection) {
    return null;
  }

  if (SlateRange.isCollapsed(editor.selection)) {
    return [];
  }

  return SlateNode.fragment(editor, editor.selection);
};

/**
 * Get Slate fragment before the current selection
 * @param {import('slate').Editor} editor - Slate editor instance
 * @returns {import('slate').Descendant[]} - Slate fragment
 */
export const getSlateFragmentBeforeSelection = (editor) => {
  if (!editor?.selection) {
    return null;
  }

  const selectionEdges = SlateRange.edges(editor.selection);
  return getSlateFragmentBefore(editor, selectionEdges[0]);
};

/**
 * Get Slate fragment after the current selection
 * @param {import('slate').Editor} editor - Slate editor instance
 * @returns {import('slate').Descendant[]} - Slate fragment
 */
export const getSlateFragmentAfterSelection = (editor) => {
  if (!editor?.selection) {
    return null;
  }

  const selectionEdges = SlateRange.edges(editor.selection);
  return getSlateFragmentAfter(editor, selectionEdges[1]);
};

/**
 * Get Slate nodes that are part of the current selection
 * @param {import('slate').Editor} editor - Slate editor instance
 * @returns {import('slate').Node[]} - Slate nodes
 */
export const getSlateNodeEntriesAtSelection = (editor) => {
  if (!editor?.selection) {
    return null;
  }

  if (SlateRange.isCollapsed(editor.selection)) {
    return [];
  }

  return [
    ...SlateEditor.nodes(editor, {
      at: editor.selection,
      match: (node) => !SlateEditor.isEditor(node) && SlateEditor.isBlock(editor, node),
    }),
  ];
};

export const characterBefore = (editor, basePoint) => {
  const beforePoint = SlateEditor.before(editor, basePoint, { unit: 'character' });
  if (!beforePoint) {
    return null;
  }
  return SlateEditor.string(editor, { anchor: beforePoint, focus: basePoint });
};

export const characterAfter = (editor, basePoint) => {
  const afterPoint = SlateEditor.after(editor, basePoint, { unit: 'character' });
  if (!afterPoint) {
    return null;
  }
  return SlateEditor.string(editor, { anchor: basePoint, focus: afterPoint });
};
